import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../../Axios";
import Confetti from "react-confetti";
import { toast } from "react-toastify";
import useConfirm from "../../../../hooks/useConfirm";

const TutorialPage = () => {
  const { program_id, slug } = useParams();
  const navigate = useNavigate();

  const [menu, setMenu] = useState([]);
  const [modules, setModules] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [moduleStatus, setModuleStatus] = useState({});

  const fetchMenu = async () => {
    try {
      const res = await api.get(`/lnd/user/elr/get-module-menu`, {
        params: { program_id },
      });

      const menuData = res?.data?.data?.data || [];
      setMenu(menuData);
      setModuleStatus(res?.data?.data?.moduleStatus || {});

      const index = menuData.findIndex((m) => m.slug === slug);
      if (index !== -1) setActiveIndex(index);
    } catch (err) {
      console.error(err);
    }
  };

  /* Fetch Menu */
  useEffect(() => {

    fetchMenu();
  }, [program_id, slug]);

  const fetchModule = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/lnd/user/elr/get-module-by-program_id`, {
        params: { program_id, slug },
      });

      setModules(res?.data?.data?.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) fetchModule();
  }, [program_id, slug]);

  const progress =
    menu.length > 0
      ? Math.round(((activeIndex + 1) / menu.length) * 100)
      : 0;

  const goNext = async () => {
    if (activeIndex < menu.length - 1) {

      const nextSlug = menu[activeIndex + 1].slug;

      navigate(
        `/learning-and-development/user-learning-program/tutorial/${program_id}/${nextSlug}`
      );
    }
  };

  const goPrev = () => {
    if (activeIndex > 0) {
      const prevSlug = menu[activeIndex - 1].slug;

      navigate(
        `/learning-and-development/user-learning-program/tutorial/${program_id}/${prevSlug}`
      );
    }
  };

  const { confirm } = useConfirm();
  const markModuleDone = async () => {

    setProgressLoading(true);
    try {

      const isConfirmed = await confirm({
        title: "Confirm Module Completion",
        content: `Are you sure you want to complete this module?`,
        confirmText: "Yes, Complete",
        cancelText: "Cancel"
      });
      if (!isConfirmed) {
        setProgressLoading(false);
        return;
      }

      const module_id = menu[activeIndex]?.id;

      await api.put(`/lnd/user/program-progress-update`, null, {
        params: {
          program_id,
          module_id,
          status: "completed",
        },
      });

      if (slug) {
        fetchModule();
        fetchMenu();
      };
    } catch (err) {
      console.error("Progress update failed", err);
    }
    finally {
      setProgressLoading(false);
    }
  };

  /* Finish Program */
  const handleFinish = async () => {

    try {
      const res = await api.post(
        "/lnd/user/add-skill-from-program-completion",
        { program_id }
      );

      // await markModuleDone();

      setShowCelebration(true);

      setTimeout(() => {
        navigate("/learning-and-development/user-learning-program");
      }, 3000);
    }
    catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add skill");
    }
  };

  const formatDescription = (html) => {
    return html.replace(
      /<oembed url="https:\/\/www\.youtube\.com\/watch\?v=([^"]+)"><\/oembed>/g,
      `<iframe width="560" height="315"
      src="https://www.youtube.com/embed/$1"
      frameborder="0"
      allowfullscreen>
    </iframe>`
    );
  };

  return (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        minHeight: "100vh",
        background: "#f5f7fb",
      }}
    >
      {/* Celebration */}
      {showCelebration && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            color: "white",
            textAlign: "center",
          }}
        >
          <Confetti numberOfPieces={400} recycle={false} />

          <h1 style={{ fontSize: 40, marginBottom: 10 }}>
            🎉 Congratulations!
          </h1>

          <p style={{ fontSize: 20 }}>
            You have successfully completed this program
          </p>

          <p style={{ opacity: 0.8 }}>Redirecting...</p>
        </div>
      )}

      <div
        style={{
          padding: "16px 25px",
          borderBottom: "1px solid #e5e7eb",
          background: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() =>
              navigate("/learning-and-development/user-learning-program")
            }
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "1px solid #d1d5db",
              background: "#f5f7fb",
              cursor: "pointer",
              fontSize: 20,
              color: "#2563eb",
            }}
          >
            ←
          </button>

          <h3 style={{ margin: 0 }}>Learning Program</h3>
        </div>

        <div>
          Module {activeIndex + 1} / {menu.length}
        </div>
      </div>

      <div style={{ height: 5, background: "#e5e7eb" }}>
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "#2563eb",
            transition: "0.3s",
          }}
        />
      </div>

      <div className="tutorial-flex-wrapper">
        <div
          className="tutorial-sidebar"
          style={{
            width: 270,
            background: "white",
            borderRight: "1px solid #e5e7eb",
            padding: 15,
            overflowY: "auto",
          }}
        >
          {menu.map((item, index) => (
            <div
              key={item.id}
              onClick={() =>
                navigate(
                  `/learning-and-development/user-learning-program/tutorial/${program_id}/${item.slug}`
                )
              }
              style={{
                padding: "10px 12px",
                marginBottom: 8,
                borderRadius: 6,
                cursor: "pointer",
                background:
                  index === activeIndex ? "#e8f0ff" : "transparent",
                border:
                  index === activeIndex
                    ? "1px solid #2563eb"
                    : "1px solid transparent",
              }}
            >
              {item?.moduleProgress?.status === "completed" ? "✓ " : ""}
              {index + 1}. {item.title}
            </div>
          ))}
        </div>

        {/* CONTENT */}
        <div
          className="tutorial-content"
          style={{
            flex: 1,
            padding: 30,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 30,
              minHeight: 400,
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              position: "relative",
            }}
          >
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="spinner" />
              </div>
            )}

            {!loading && modules?.title && (
              <>
                <h2 style={{ marginBottom: 20 }}>{modules.title}</h2>

                <div
                  dangerouslySetInnerHTML={{
                    __html: formatDescription(modules.description),
                  }}
                />

                <div style={{ marginTop: 25 }}>
                  <button
                    onClick={modules?.moduleProgress?.status !== "completed" ? markModuleDone : undefined}
                    disabled={modules?.moduleProgress?.status === "completed" || progressLoading}
                    style={{
                      padding: "7px 14px",
                      borderRadius: 6,
                      border: "none",
                      background: "#16a34a",
                      color: "white",
                      fontSize: "13px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: modules?.moduleProgress?.status === "completed" ? "default" : "pointer",
                      opacity: modules?.moduleProgress?.status === "completed" ? 0.8 : 1,
                    }}
                  >
                    ✓ {modules?.moduleProgress?.status === "completed" ? "Completed" : "Mark as Completed"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* NAVIGATION */}
          <div
            style={{
              marginTop: 25,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <button
              disabled={activeIndex === 0 || progressLoading}
              onClick={goPrev}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "white",
              }}
            >
              Previous
            </button>

            {(activeIndex === menu.length - 1 && moduleStatus.status !== "completed") && (
              <button
                onClick={handleFinish}
                style={{
                  padding: "10px 20px",
                  borderRadius: 6,
                  border: "none",
                  background: "green",
                  color: "white",
                }}
              >
                Finish
              </button>
            )}
            {(activeIndex !== menu.length - 1) && (
              <button
                onClick={goNext}
                disabled={progressLoading}
                style={{
                  padding: "10px 20px",
                  borderRadius: 6,
                  border: "none",
                  background: "#2563eb",
                  color: "white",
                }}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loader CSS */}
      <style>
        {`
        .spinner {
          width:40px;
          height:40px;
          border:4px solid #e5e7eb;
          border-top:4px solid #2563eb;
          border-radius:50%;
          animation:spin 0.8s linear infinite;
        }

        @keyframes spin {
          from {transform: rotate(0deg)}
          to {transform: rotate(360deg)}
        }

        /* Responsive styles for TutorialPage */
        .tutorial-flex-wrapper {
          display: flex;
          height: calc(100vh - 40px);
        }
        .tutorial-sidebar {
          min-width: 200px;
          max-width: 320px;
        }
        .tutorial-content {
          min-width: 0;
        }
        @media (max-width: 900px) {
          .tutorial-flex-wrapper {
            flex-direction: column;
            height: auto;
          }
          .tutorial-sidebar {
            width: 100% !important;
            max-width: 100vw;
            min-width: 0;
            border-right: none !important;
            border-bottom: 1px solid #e5e7eb !important;
            padding: 10px !important;
            overflow-x: auto;
            overflow-y: visible;
          }
          .tutorial-content {
            width: 100% !important;
            padding: 16px !important;
          }
        }
        @media (max-width: 600px) {
          .tutorial-content {
            padding: 8px !important;
          }
          .tutorial-sidebar {
            padding: 6px !important;
          }
          .tutorial-content h2 {
            font-size: 1.2rem !important;
          }
        }
        `}
      </style>
    </div>
  );
};

export default TutorialPage;